import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import TinyWinBanner from "../../components/signals/TinyWinBanner";
import LoadingState from "../../components/common/LoadingState";
import SprintChatPanel from "./components/SprintChatPanel";
import SprintDetailsCard from "./components/SprintDetailsCard";
import SprintEmptyState from "./components/SprintEmptyState";
import SprintHeroCard from "./components/SprintHeroCard";
import SprintSharedContextCard from "./components/SprintSharedContextCard";
import SprintWeekPatternCard from "./components/SprintWeekPatternCard";
import ChallengeShowUpCard from "./components/ChallengeShowUpCard";
import { useInterventionCard } from "../../intervention/useInterventionCard";
import { useSprintWorkspace } from "./hooks/useSprintWorkspace";
import { getPartnerMessageLabel } from "../../navigation/actionIntents";

function resolveSprintAction(role, actions, workspace) {
  if (role === "do_move") return actions.doOneStep;
  if (role === "view_week") return actions.viewWeek;
  if (role === "renew_sprint") return actions.renew;
  return actions.messagePartner;
}

export default function SprintPage() {
  const { loading, error, actionFeedback, intervention, workspace, actions, composerFocusKey, refresh, openConnections, openNetwork } = useSprintWorkspace();
  const { beginPrimaryAction: beginInterventionPrimaryAction } = useInterventionCard({
    intervention,
    surface: "sprint",
  });

  const sprintPrimaryAction = !workspace
    ? null
    : {
          label: workspace.sprintPrimaryActionLabel || getPartnerMessageLabel(workspace.partnerName),
          onClick: async () => {
            if (workspace.primaryActionKey) {
              await beginInterventionPrimaryAction();
            }
            resolveSprintAction(workspace.sprintPrimaryActionRole, actions, workspace)?.();
          },
        };

  const sprintSecondaryAction = !workspace
    ? null
    : workspace.sprintSecondaryActionLabel && workspace.sprintSecondaryActionRole
      ? {
          label: workspace.sprintSecondaryActionLabel || "Do my part",
          onClick: resolveSprintAction(workspace.sprintSecondaryActionRole, actions, workspace),
        }
      : null;

  return (
    <Box sx={{ maxWidth: 1360, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "flex-start",
          gap: 2,
          mb: 2.3,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Typography
          variant="h2"
          sx={{ fontSize: { xs: "1.8rem", lg: "2.1rem" }, fontWeight: 850, letterSpacing: 0, color: "#102033" }}
        >
          Accountability
        </Typography>
        <Button
          variant="text"
          onClick={workspace ? actions?.openNetwork : openNetwork}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            color: "#17345d",
            px: 0,
            minWidth: "auto",
            "&:hover": { backgroundColor: "transparent", opacity: 0.86 },
          }}
        >
          Activity
        </Button>
      </Box>

      {actionFeedback ? <TinyWinBanner text={actionFeedback} sx={{ mb: 2 }} /> : null}

      {loading ? (
        <LoadingState
          title="Preparing Accountability"
          body="Checking the agreement, Daily Reports, and latest activity."
          rows={3}
          sx={{ maxWidth: 720 }}
        />
      ) : error && !workspace ? (
        <Typography color="text.secondary">{error}</Typography>
      ) : !workspace ? (
        <SprintEmptyState onOpenConnections={openConnections} />
      ) : (
        <Stack spacing={2.4}>
          <SprintSharedContextCard
            focusLine={workspace.focusLine}
            partnerFocusLine={workspace.partnerFocusLine}
            partnerName={workspace.partnerName}
            onViewPlan={actions.openPlan}
            onViewPartnerFocus={actions.openPartnerFocus}
          />

          <SprintHeroCard
            state={workspace.state}
            modeLabel={workspace.modeLabel}
            stateLabel={workspace.stateLabel}
            title={workspace.title}
            body={workspace.body}
            partnerContextLine={workspace.partnerContextLine}
            supportingLine={workspace.supportingLine}
            ring={workspace.ring}
            challengeScore={workspace.challengeScore}
            sprintMode={workspace.sprintMode}
            partnerName={workspace.partnerName}
            primaryAction={sprintPrimaryAction}
            secondaryAction={sprintSecondaryAction}
          />

          <ChallengeShowUpCard
            partnerName={workspace.partnerName}
            sprintMode={workspace.sprintMode}
            viewerStatusToday={workspace.viewerStatusToday}
            partnerStatusToday={workspace.partnerStatusToday}
            onShowUpStatus={actions.showUpStatus}
            onDoOneStep={actions.doOneStep}
            onAttachPhotoProof={actions.attachPhotoProof}
            onSubmitTimerReport={actions.submitTimerReport}
            onAcknowledgePartnerReport={actions.acknowledgePartnerReport}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1.7fr) minmax(320px, 0.92fr)" },
              gap: 2.25,
              alignItems: "start",
            }}
          >
            <Stack spacing={2.05} sx={{ minWidth: 0 }}>
              <Box id="sprint-week-pattern">
                <SprintWeekPatternCard
                  items={workspace.weekPattern}
                  partnerName={workspace.partnerName}
                  recentItems={workspace.recentMovement}
                  sprintMode={workspace.sprintMode}
                />
              </Box>
              <SprintChatPanel
                sprintId={workspace.sprintId}
                connectionId={workspace.networkConnectionId}
                partnerName={workspace.partnerName}
                partnerUserId={workspace.partnerUserId}
                quickReplies={workspace.quickReplies}
                composerFocusKey={composerFocusKey}
                disabled={workspace.isLocked}
                onActivity={refresh}
              />
            </Stack>

            <Stack spacing={2.05} sx={{ minWidth: 0 }}>
              <SprintDetailsCard
                lifecycleSummary={workspace.lifecycleSummary}
                startLabel={workspace.startLabel}
                endLabel={workspace.endLabel}
                endChipLabel={workspace.endChipLabel}
                visionTitle={workspace.visionTitle}
                renewLabel={workspace.renewLabel}
                canRenew={workspace.canRenew}
                canClose={workspace.canClose}
                onRenew={actions.renew}
                onClose={actions.close}
                onDisconnect={actions.disconnect}
                onBlock={actions.block}
                onManageSprint={actions.openPlan}
              />
            </Stack>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
